/**
 * Centralised axios instance for the Taskydo React app.
 *
 * Subdomain-aware, multi-tenant safe.
 * --------------------------------------------------------------------------
 *  - `baseURL` keeps the organisation label and always uses the Taskydo
 *    apex (`psfl.curedia.com` calls `https://psfl.tasky.do/api`).
 *    django-tenants still picks the schema from that Host header.
 *    A bare `https://tasky.do` with no organisation label would hit the
 *    public schema, so the label is preserved.
 *
 *  - We DO NOT send a tenant / organisation header from the client.  The
 *    subdomain (Host) is the single source of truth and the browser sets
 *    it automatically.
 *
 *  - Auth: a request interceptor attaches `Authorization: Bearer <access>`
 *    when an access token is in localStorage.  A response interceptor
 *    catches a single 401, attempts ONE token refresh against the actual
 *    SimpleJWT refresh endpoint (`/api/super_admin/token/refresh/` — see
 *    `taskydo/urls_tenant.py` + `super_admin/api_urls.py`), then retries
 *    the original request.  If refresh fails, we wipe localStorage and
 *    bounce the user to `/login`.
 */
import axios from 'axios';

import { STORAGE_KEYS, apiOrigin } from '../config';
import { notify } from '../lib/notifications';
import { formatApiError } from '../lib/errors';
import { isAuthHandshakeUrl } from '../lib/authHandshake';
import { clearLocalAuth, isSignedOut, writeTokens } from '../lib/authStorage';
import { redirectToLogin } from '../lib/sessionRedirect';

// ---------------------------------------------------------------------------
// Endpoint paths.  Hardcoded here because they are NOT environment-specific
// — they are part of the Django URLConf contract.  Confirmed against:
//   * super_admin/api_urls.py        (path('token/'), path('token/refresh/'))
//   * taskydo/urls_tenant.py         (mounts super_admin api at /api/super_admin/)
//   * taskydo/urls_public.py         (same mount on public host)
// ---------------------------------------------------------------------------
const TOKEN_OBTAIN_PATH  = '/super_admin/token/';          // -> POST /api/super_admin/token/
const TOKEN_REFRESH_PATH = '/super_admin/token/refresh/';  // -> POST /api/super_admin/token/refresh/

// ---------------------------------------------------------------------------
// baseURL — same-origin, subdomain-preserving.
// `window.location.origin` is e.g. "https://tasky.do" on the public host
// and "https://acme.tasky.do" on the acme tenant — exactly what django-
// tenants needs to resolve the schema.  Falls back to '/api' if window is
// somehow unavailable (SSR / unit tests).
// ---------------------------------------------------------------------------
function buildBaseURL() {
  const origin = apiOrigin();
  if (origin) return `${origin}/api`;
  return '/api';
}

const api = axios.create({
  baseURL: buildBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  // We send cookies too so a user who logged into the Django HTML site
  // (session auth) is auto-recognised by the React app.  Tenant-safe
  // because the cookie is scoped to the current host (the subdomain).
  withCredentials: true,
  timeout: 30_000,

  // --------------------------------------------------------------------
  // Django CSRF compatibility.
  //
  // When there is no usable JWT in localStorage (expired access token, or
  // the user signed in via the legacy HTML login form which only sets the
  // session cookie) DRF's auth chain falls through from
  // ``TenantAwareJWTAuthentication`` to ``SessionAuthentication``.  Session
  // auth enforces CSRF on every unsafe method (POST/PUT/PATCH/DELETE) by
  // comparing the ``X-CSRFToken`` request header against the ``csrftoken``
  // cookie.
  //
  // Axios's defaults follow the Angular convention (``XSRF-TOKEN`` cookie →
  // ``X-XSRF-TOKEN`` header), which Django does NOT recognise.  Without
  // these overrides every Pharma SFA write (e.g. the territory "Apply
  // plan" rebalance, DCR submit, tour-program approve) is rejected with
  // ``CSRF Failed: CSRF token missing`` whenever the request lands on the
  // session-auth path.
  //
  // Mirrors the same setting on the shared ``src/lib/api.js`` instance.
  // No-op for the JWT path (Bearer-auth requests are CSRF-exempt in DRF).
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});


export const TOKEN_ENDPOINTS = {
  OBTAIN:  TOKEN_OBTAIN_PATH,
  REFRESH: TOKEN_REFRESH_PATH,
};

// ---------------------------------------------------------------------------
// Request interceptor — attach JWT on every outbound call.
// ---------------------------------------------------------------------------
api.interceptors.request.use((config) => {
  if (isSignedOut()) {
    if (config.headers) {
      delete config.headers.Authorization;
    }
    return config;
  }
  const access = localStorage.getItem(STORAGE_KEYS.ACCESS);
  if (access) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — single-shot 401 → refresh → retry.
//
// We deliberately use a module-level promise to coalesce parallel refreshes:
// if 5 requests fire at once and all 5 get a 401, only ONE refresh hits the
// server; the other 4 await the same promise.
// ---------------------------------------------------------------------------
let refreshInFlight = null;

function shouldBounceOn401(url) {
  return !isAuthHandshakeUrl(url) && !isSignedOut();
}

/**
 * Should this error produce a global toast?  Mirrors the rule used in
 * `src/lib/api.js` so behaviour is identical no matter which axios
 * instance fired the request.  Skipped when:
 *   - caller passed `silent: true` on the request config,
 *   - it's a 401 we are about to silently refresh,
 *   - it's an expected unauth probe (`/login`, `/me`, refresh).
 */
function shouldToast(error) {
  const cfg    = error.config || {};
  const status = error.response && error.response.status;
  const url    = (cfg.url || '').toString();

  if (cfg.silent) return false;

  // Auth endpoints render their failures inline on the LoginPage
  // (`<ErrorBox/>`), so toasting them too would be a noisy duplicate.
  // Refresh-token failures get the dedicated `Session expired` toast.
  if (url.includes(TOKEN_REFRESH_PATH))   return false;
  if (url.includes(TOKEN_OBTAIN_PATH))    return false;
  if (url.includes('/super_admin/me'))    return false;       // bootstrap probe
  if (url.includes('/super_admin/login')) return false;       // login page handles its own message
  if (url.includes('/super_admin/password-reset')) return false;
  if (url.includes('/super_admin/logout'))return false;

  if (status === 401) {
    // For other 401s we only toast AFTER a refresh attempt has been tried.
    if (!cfg.__isRetry && localStorage.getItem(STORAGE_KEYS.REFRESH)) {
      return false;
    }
  }
  return true;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status   = error.response && error.response.status;
    const url      = (original.url || '').toString();
    const handshake = isAuthHandshakeUrl(url);
    const refresh  = isSignedOut() ? null : localStorage.getItem(STORAGE_KEYS.REFRESH);

    // Don't try to refresh against the refresh endpoint itself, or if we've
    // already retried once for this request, or if there is no refresh
    // token to begin with.
    const isRefreshCall = original.url && original.url.includes(TOKEN_REFRESH_PATH);

    if (status !== 401 || original.__isRetry || isRefreshCall || !refresh || isSignedOut()) {
      if (status === 401 && !handshake && shouldBounceOn401(url)) {
        clearLocalAuth();
        redirectToLogin();
      }
      if (shouldToast(error)) {
        // eslint-disable-next-line no-console
        console.error('[api]', original.method?.toUpperCase(), original.url, error);
        notify.fromAxios(error, {
          title: status ? `API error ${status}` : 'API error',
        });
      }
      return Promise.reject(error);
    }

    try {
      if (!refreshInFlight) {
        refreshInFlight = axios
          .post(
            buildBaseURL() + TOKEN_REFRESH_PATH,
            { refresh },
            {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true,
            },
          )
          .then((r) => {
            if (isSignedOut()) throw new Error('signed out during refresh');
            const newAccess = r.data && r.data.access;
            if (!newAccess) throw new Error('Refresh response missing `access` token');
            writeTokens({ access: newAccess, refresh: r.data.refresh });
            return newAccess;
          })
          .finally(() => {
            refreshInFlight = null;
          });
      }

      const newAccess = await refreshInFlight;

      original.__isRetry = true;
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newAccess}`;
      return api(original);
    } catch (refreshError) {
      // Refresh genuinely failed — log the user out and tell them why.
      notify.error(formatApiError(refreshError), { title: 'Session expired' });
      clearLocalAuth();
      if (shouldBounceOn401(url)) redirectToLogin();
      return Promise.reject(refreshError);
    }
  },
);

export default api;
